import { api } from "@/api/client";
import type { DependencyHealth } from "@/api/types";
import { InfoDot, PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t, tOr } from "@/i18n";
import { formatInt, formatPercent, formatRelative } from "@/lib/format";
import { includeTestSessions, selectedTenant, selectedWindow } from "@/state/filters";

export function HealthView() {
  const health = usePoll((s) => api.health(s), []);

  const snapshot = usePoll(
    (s) =>
      api.snapshot(
        {
          window: selectedWindow.value,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [selectedWindow.value, includeTestSessions.value, selectedTenant.value],
  );

  return (
    <div style="display: grid; gap: 24px;">
      <section class="card">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h2>{t("health.dependencies")}</h2>
          <Staleness
            lastFetched={health.lastFetched}
            loading={health.loading}
            error={health.error}
            onRefresh={health.refetch}
          />
        </div>
        <PanelHelp k="health.dependencies" />
        {/* The health probe has no partner or window parameter — say so, or
            the selector above this card implies a scope that isn't applied. */}
        <div class="kpi-sub" style="margin-bottom: 12px;">
          {t("health.dependenciesScope")}
        </div>
        {health.error && <div class="error-banner">{t("health.backendUnreachable")}</div>}
        {health.data && <DependencyStrip deps={health.data.dependencies} />}
        {health.data && (
          <div class="muted" style="margin-top: 12px;">
            {t("health.overall")}&nbsp;
            <span class={`pill ${health.data.status}`}>
              {tOr(`status.${health.data.status}`, health.data.status)}
            </span>
            &nbsp;·&nbsp;{t("health.version", { v: health.data.version })}
          </div>
        )}
      </section>

      <section class="card">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
          <h2>{t("health.northStar")}</h2>
          <Staleness
            lastFetched={snapshot.lastFetched}
            loading={snapshot.loading}
            error={snapshot.error}
            onRefresh={snapshot.refetch}
          />
        </div>
        <PanelHelp k="health.northStar" />
        <div class="grid grid-3">
          <KpiCard
            label={t("health.turnsInWindow")}
            value={formatInt(snapshot.data?.north_star.turns_total ?? 0)}
            sub={snapshot.data?.window_label ?? ""}
            info="health.turns_total"
          />
          <KpiCard
            label={t("health.engagedSessions")}
            value={formatInt(snapshot.data?.north_star.sessions_engaged_total ?? 0)}
            sub={t("health.engagedSub")}
            info="health.sessions_engaged"
          />
          <KpiCard
            label={t("health.feedbackRatio")}
            value={
              snapshot.data?.north_star.feedback_positive_ratio == null
                ? "—"
                : formatPercent(snapshot.data.north_star.feedback_positive_ratio)
            }
            sub={t("health.ratings", { n: snapshot.data?.north_star.feedback_count ?? 0 })}
            info="health.feedback_positive_ratio"
          />
        </div>
      </section>

      <section class="card">
        <h2>
          {t("health.anomalies")} <InfoDot k="health.anomalies" />
        </h2>
        <PanelHelp k="health.anomalies" />
        {!snapshot.data && <div class="muted">{t("common.loading")}</div>}
        {snapshot.data && snapshot.data.anomalies.length === 0 && (
          <div class="muted">
            {t("health.allClear", { window: snapshot.data.window_label })}
          </div>
        )}
        {snapshot.data && (
          <div style="display: grid; gap: 12px;">
            {snapshot.data.anomalies.map((a) => (
              <div
                key={a.kind}
                class={`anomaly ${a.severity === "critical" ? "critical" : ""}`}
              >
                <div style="flex: 1;">
                  <div class="kind">{tOr(`anomaly.${a.kind}`, a.kind)}</div>
                  <div class="detail">{a.detail}</div>
                </div>
                <div class="muted" style="font-size: 13px;">
                  <span class="hl-orange">{tOr(`severity.${a.severity}`, a.severity)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function DependencyStrip({ deps }: { deps: DependencyHealth[] }) {
  return (
    <div class="dep-strip">
      {deps.map((d) => (
        <div key={d.name} class={`dep-card ${d.status}`}>
          <div class="dep-name">
            <span title={d.name}>{tOr(`dep.${d.name}`, d.name)}</span>
            <span class={`pill ${d.status}`}>{tOr(`status.${d.status}`, d.status)}</span>
          </div>
          <div class="dep-detail">
            {d.latency_ms != null && <>{t("health.dep.latency", { ms: d.latency_ms })} · </>}
            {d.last_success_at && (
              <>{t("health.dep.last", { when: formatRelative(d.last_success_at) })}</>
            )}
            {!d.latency_ms && !d.last_success_at && d.detail}
          </div>
          {d.latency_ms != null && d.last_success_at && d.detail && (
            <div class="dep-detail muted">{d.detail}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function KpiCard({
  label,
  value,
  sub,
  info,
}: {
  label: string;
  value: string;
  sub?: string;
  info?: string;
}) {
  return (
    <div class="card" style="border-left: 4px solid var(--brand-orange);">
      <h3>
        {label}
        {info && <InfoDot k={info} />}
      </h3>
      <div class="kpi">{value}</div>
      {sub && <div class="kpi-sub">{sub}</div>}
    </div>
  );
}
